import type { ReactNode } from 'react'

import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import SimpleSelect from '@/components/app/simple-select'

interface PaginationProps {
    page: number
    pageSize: number
    total: number
    onPageChange: (page: number) => void
    onPageSizeChange?: (size: number) => void
    itemLabel?: string
    className?: string
}

const PAGE_SIZES = ['10', '20', '50']

const pageList = (page: number, pages: number): Array<number | 'gap'> => {
    if (pages <= 7) return Array.from({ length: pages }, (_, index) => index + 1)
    const items: Array<number | 'gap'> = [1]
    const start = Math.max(2, page - 1)
    const end = Math.min(pages - 1, page + 1)
    if (start > 2) items.push('gap')
    for (let index = start; index <= end; index += 1) items.push(index)
    if (end < pages - 1) items.push('gap')
    items.push(pages)
    return items
}

const Pagination = ({ page, pageSize, total, onPageChange, onPageSizeChange, itemLabel = 'records', className }: PaginationProps): ReactNode => {
    const pages = Math.max(1, Math.ceil(total / pageSize))
    const first = total === 0 ? 0 : (page - 1) * pageSize + 1
    const last = Math.min(total, page * pageSize)

    return (
        <div className={cn('flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between print:hidden', className)}>
            <div className='flex items-center gap-3 text-sm text-muted-foreground'>
                <span>
                    Showing <span className='font-medium text-foreground'>{first}</span> to <span className='font-medium text-foreground'>{last}</span> of <span className='font-medium text-foreground'>{total}</span> {itemLabel}
                </span>

                {onPageSizeChange && (
                    <SimpleSelect
                        value={String(pageSize)}
                        onValueChange={value => onPageSizeChange(Number(value))}
                        options={PAGE_SIZES.map(size => ({ value: size, label: `${size} per page` }))}
                        size='sm'
                        className='hidden w-32 sm:flex'
                        ariaLabel='Rows per page'
                    />
                )}
            </div>

            <nav className='flex items-center gap-1' aria-label='Pagination'>
                <Button
                    variant='outline'
                    size='icon-sm'
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    aria-label='Previous page'
                >
                    <ChevronLeft />
                </Button>

                {pageList(page, pages).map((item, index) => item === 'gap'
                    ? <span key={`gap-${index}`} className='px-1 text-sm text-muted-foreground'>...</span>
                    : (
                        <Button
                            key={item}
                            variant={item === page ? 'default' : 'ghost'}
                            size='icon-sm'
                            onClick={() => onPageChange(item)}
                            aria-label={`Page ${item}`}
                            aria-current={item === page ? 'page' : undefined}
                        >
                            {item}
                        </Button>
                    ))}

                <Button
                    variant='outline'
                    size='icon-sm'
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= pages}
                    aria-label='Next page'
                >
                    <ChevronRight />
                </Button>
            </nav>
        </div>
    )
}

export default Pagination
